/**
 * Month-range helpers for the /spending page's month picker.
 *
 * `monthRange("2026-05")` returns ISO-date strings for the first +
 * last day of May 2026. Pure UTC; no timezone subtleties because the
 * stored `date` field is YYYY-MM-DD without a TZ.
 */

const MONTH_RE = /^\d{4}-\d{2}$/;

export interface MonthRange {
  /** Inclusive lower bound, ISO YYYY-MM-DD. */
  from: string;
  /** Inclusive upper bound, ISO YYYY-MM-DD. */
  to: string;
}

export function monthRange(yearMonth: string): MonthRange {
  if (!MONTH_RE.test(yearMonth)) {
    throw new Error(`monthRange: expected YYYY-MM, got "${yearMonth}"`);
  }
  const [yStr, mStr] = yearMonth.split("-");
  const y = Number(yStr);
  const m0 = Number(mStr) - 1; // 0-11
  // Last day of (y, m0): pass day=0 of next month → JS clamps to last
  // day of target month. Handles leap years natively (Feb 2024 → 29).
  const lastDay = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  return {
    from: `${yStr}-${pad2(m0 + 1)}-01`,
    to: `${yStr}-${pad2(m0 + 1)}-${pad2(lastDay)}`,
  };
}

/** YYYY-MM string for "today" UTC. */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getUTCFullYear().toString().padStart(4, "0")}-${pad2(
    now.getUTCMonth() + 1,
  )}`;
}

/** Step a YYYY-MM string by `delta` months. e.g. ("2026-05", -1) → "2026-04". */
export function shiftMonth(yearMonth: string, delta: number): string {
  if (!MONTH_RE.test(yearMonth)) {
    throw new Error(`shiftMonth: expected YYYY-MM, got "${yearMonth}"`);
  }
  const [yStr, mStr] = yearMonth.split("-");
  const y = Number(yStr);
  const m1 = Number(mStr); // 1-12
  const total0 = (y * 12 + (m1 - 1)) + delta;
  const newY = Math.floor(total0 / 12);
  const newM1 = (total0 % 12) + 1;
  return `${newY.toString().padStart(4, "0")}-${pad2(newM1)}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
